import type { AccountProfile, RoomRecordingLibrary, RoomSummary, ScheduledEventSummary, SessionDetail, SessionSummary } from "@shared/api";
import type { TrackManifest } from "@shared/manifest";
import type { RenderScene } from "@shared/render";
import type { RecordingMode } from "@shared/recording";
import type { ClerkTokenGetter, ClerkTokenOptions } from "./clerkSession";

/**
 * Typed API client. Host endpoints authenticate via a Clerk session token
 * (Authorization Bearer) or the same-origin session cookie, or the dev header
 * locally. Participant endpoints use the short-lived participant token.
 */

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

async function parseError(res: Response): Promise<never> {
  let code = "unknown";
  let message = `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as { error?: { code: string; message: string } };
    if (body.error) {
      code = body.error.code;
      message = body.error.message;
    }
  } catch {
    // non-JSON error body
  }
  throw new ApiRequestError(res.status, code, message);
}

let hostTokenProvider: ClerkTokenGetter | null = null;

/** Clerk session token getter used by host API calls when Clerk is active. */
export function setHostTokenProvider(provider: ClerkTokenGetter | null): void {
  hostTokenProvider = provider;
}

async function hostHeaders(tokenOptions?: ClerkTokenOptions): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  if (import.meta.env.DEV && (!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || import.meta.env.VITE_CLERK_DISABLED === "1")) {
    headers["x-dev-user"] = localStorage.getItem("devUser") ?? "matt@dev.local";
  }
  const token = await hostTokenProvider?.(tokenOptions);
  if (token) headers.authorization = `Bearer ${token}`;
  return headers;
}

async function hostRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const request = (authHeaders: Record<string, string>) => fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: { ...authHeaders, ...(init.headers ?? {}) },
  });
  const initialHeaders = await hostHeaders();
  let res = await request(initialHeaders);
  if (res.status === 401 && hostTokenProvider) {
    const refreshedHeaders = await hostHeaders({ skipCache: true });
    if (
      refreshedHeaders.authorization
      && refreshedHeaders.authorization !== initialHeaders.authorization
    ) {
      res = await request(refreshedHeaders);
    }
  }
  return res;
}

async function hostFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await hostRequest(path, init);
  if (!res.ok) await parseError(res);
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new ApiRequestError(res.status, "unauthorized", "Host authentication required");
  }
  return (await res.json()) as T;
}

async function participantFetch<T>(token: string, path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) await parseError(res);
  return (await res.json()) as T;
}

async function publicFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, { ...init, credentials: "same-origin" });
  if (!res.ok) await parseError(res);
  return (await res.json()) as T;
}

// --- Host API ---------------------------------------------------------------

export const hostApi = {
  getAccount: () => hostFetch<AccountProfile>("/api/account"),
  updateAccount: (displayName: string) =>
    hostFetch<AccountProfile>("/api/account", {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ displayName }),
    }),
  listSessions: () => hostFetch<{ sessions: SessionSummary[] }>("/api/sessions"),
  createSession: (title: string, recordingMode: RecordingMode = "audio_video") =>
    hostFetch<{ id: string }>("/api/sessions", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title, recordingMode }),
    }),
  listRooms: () => hostFetch<{ username: string; rooms: RoomSummary[] }>("/api/sessions/rooms"),
  getRoomLibrary: (roomId: string) =>
    hostFetch<RoomRecordingLibrary>(`/api/sessions/rooms/${roomId}`),
  createRoom: (title: string, recordingMode: RecordingMode = "audio_video") =>
    hostFetch<RoomSummary>("/api/sessions/rooms", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title, recordingMode }),
    }),
  updateRoom: (roomId: string, settings: { title?: string; recordingMode?: RecordingMode }) =>
    hostFetch<{ id: string; title: string; recordingMode: RecordingMode }>(`/api/sessions/rooms/${roomId}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(settings),
    }),
  deleteRoom: async (roomId: string): Promise<void> => {
    const res = await hostRequest(`/api/sessions/rooms/${roomId}`, {
      method: "DELETE",
    });
    if (!res.ok) await parseError(res);
  },
  startRoomSession: (roomId: string) =>
    hostFetch<{ id: string; title: string; existing: boolean }>(`/api/sessions/rooms/${roomId}/sessions`, {
      method: "POST",
    }),
  startNextTake: (id: string) =>
    hostFetch<{ id: string; title: string; existing: boolean }>(`/api/sessions/${id}/next-take`, {
      method: "POST",
    }),
  getSession: (id: string) => hostFetch<SessionDetail>(`/api/sessions/${id}`),
  renameSession: (id: string, title: string) =>
    hostFetch<{ id: string; title: string }>(`/api/sessions/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title }),
    }),
  deleteSession: async (id: string): Promise<void> => {
    const res = await hostRequest(`/api/sessions/${id}`, {
      method: "DELETE",
    });
    if (!res.ok) await parseError(res);
  },
  createInvite: (id: string, guestName?: string) =>
    hostFetch<{ inviteId: string; guestUrl: string; expiresAtEpochMs: number }>(
      `/api/sessions/${id}/invites`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(guestName ? { guestName } : {}),
      },
    ),
  emailSessionInvite: (sessionId: string, inviteId: string, guestEmail: string, token: string) =>
    hostFetch<{ sent: true; sentAtEpochMs: number; retryAfterSeconds: number }>(
      `/api/sessions/${sessionId}/invites/${inviteId}/email`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ guestEmail, token }),
      },
    ),
  enterStudio: (id: string) =>
    hostFetch<{
      participantId: string;
      participantToken: string;
      session: { id: string; title: string; state: string; roomId: string | null; recordingMode: RecordingMode };
    }>(`/api/sessions/${id}/enter`, { method: "POST" }),
  reverify: (id: string) =>
    hostFetch<{ sessionState: string }>(`/api/sessions/${id}/reverify`, { method: "POST" }),
  refreshCloudBackup: (id: string) =>
    hostFetch<SessionDetail["cloudBackup"]>(`/api/sessions/${id}/cloud-backup/refresh`, { method: "POST" }),
  cloudBackupToken: (id: string, kind: "video" | "audio") =>
    hostFetch<{ token: string; expiresInSeconds: number }>(`/api/sessions/${id}/cloud-backup/${kind}/download-token`, { method: "POST" }),
  downloadToken: (sessionId: string, trackId: string) =>
    hostFetch<{ token: string }>(`/api/sessions/${sessionId}/tracks/${trackId}/download-token`, {
      method: "POST",
    }),
  renderArtifactToken: (sessionId: string, artifactId: string) =>
    hostFetch<{ token: string; expiresInSeconds: number }>(
      `/api/sessions/${sessionId}/render-artifacts/${artifactId}/download-token`,
      { method: "POST" },
    ),
  listScheduledEvents: () => hostFetch<{ events: ScheduledEventSummary[] }>("/api/schedule"),
  createScheduledEvent: (payload: {
    title: string;
    scheduledStartEpochMs: number;
    scheduledEndEpochMs?: number;
    roomId?: string;
    guestEmail?: string;
    timeZone?: string;
  }) =>
    hostFetch<ScheduledEventSummary>("/api/schedule", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }),
  updateScheduledEvent: (
    id: string,
    payload: {
      title?: string;
      scheduledStartEpochMs?: number;
      scheduledEndEpochMs?: number | null;
      roomId?: string;
      guestEmail?: string | null;
      timeZone?: string;
    },
  ) =>
    hostFetch<ScheduledEventSummary>(`/api/schedule/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }),
  createScheduledEventShareLink: (id: string) =>
    hostFetch<{ guestUrl: string; expiresAtEpochMs: number }>(`/api/schedule/${id}/share-link`, {
      method: "POST",
    }),
  sendScheduledEventReminder: (id: string) =>
    hostFetch<{ sent: true; sentAtEpochMs: number; retryAfterSeconds: number }>(
      `/api/schedule/${id}/email-reminder`,
      { method: "POST" },
    ),
  deleteScheduledEvent: async (id: string): Promise<void> => {
    const res = await hostRequest(`/api/schedule/${id}`, {
      method: "DELETE",
    });
    if (!res.ok) await parseError(res);
  },
};

// --- Guest / participant API ------------------------------------------------

export const participantApi = {
  previewInvite: (token: string) =>
    publicFetch<{
      session: { id: string; title: string; state: string; recordingMode: RecordingMode };
      hostName: string | null;
    }>("/api/invites/preview", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    }),
  getRoom: (username: string, slug: string) =>
    hostFetch<{
      room: { title: string; slug: string; username: string; recordingMode: RecordingMode };
      currentSession: { id: string; title: string; state: string; recordingMode: RecordingMode } | null;
    }>(`/api/rooms/${encodeURIComponent(username)}/${encodeURIComponent(slug)}`),
  claimRoom: (username: string, slug: string, name: string, joinKey: string) =>
    hostFetch<{
      participantId: string;
      participantToken: string;
      session: { id: string; title: string; state: string; recordingMode: RecordingMode };
      name: string;
    }>(`/api/rooms/${encodeURIComponent(username)}/${encodeURIComponent(slug)}/join`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, joinKey }),
    }),
  claimInvite: (token: string, name: string) =>
    hostFetch<{
      participantId: string;
      participantToken: string;
      session: { id: string; title: string; state: string; recordingMode: RecordingMode };
      name: string;
    }>("/api/invites/claim", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token, name }),
    }),
  registerTrack: (token: string, kind: "audio" | "video", mimeType: string) =>
    participantFetch<{ trackId: string; state: string; mimeType: string }>(token, "/api/tracks", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ kind, mimeType }),
    }),
  uploadSegment: async (
    token: string,
    trackId: string,
    seq: number,
    blob: Blob,
    meta: { startOffsetMs: number; durationMs: number; checksumSha256: string },
  ): Promise<{ stored: boolean; seq: number; duplicate?: boolean }> => {
    const params = new URLSearchParams({
      startOffsetMs: String(Math.round(meta.startOffsetMs)),
      durationMs: String(Math.round(meta.durationMs)),
      checksumSha256: meta.checksumSha256,
    });
    return participantFetch(token, `/api/tracks/${trackId}/segments/${seq}?${params}`, {
      method: "PUT",
      body: blob,
    });
  },
  submitManifest: (token: string, trackId: string, manifest: Omit<TrackManifest, "sessionId" | "participantId" | "trackId">) =>
    participantFetch<{
      accepted: boolean;
      state: string;
      verification: { ok: boolean; missingSeqs: number[]; verifiedSegments: number; expectedSegments: number };
    }>(token, `/api/tracks/${trackId}/manifest`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(manifest),
    }),
  sessionStatus: (token: string) =>
    participantFetch<{
      sessionState: string;
      safeToClose: boolean;
      tracks: {
        trackId: string;
        kind: string;
        state: string;
        uploadedSegments: number;
        lastSeq: number;
        verifiedSegments: number;
        missingSeqs: number[];
      }[];
    }>(token, "/api/session/status"),
  recoverSession: (token: string) =>
    participantFetch<{ recovered: boolean; deferredUntilStop?: boolean; sessionState: string; tracks: number; rebuiltTracks: number }>(
      token,
      "/api/session/recover",
      { method: "POST" },
    ),
  preflightUpload: (token: string, payload: Uint8Array) =>
    participantFetch<{ receivedBytes: number; receivedAtEpochMs: number }>(
      token,
      "/api/session/preflight",
      { method: "POST", body: new Blob([payload.buffer as ArrayBuffer]) },
    ),
  saveRenderScene: (token: string, scene: RenderScene) =>
    participantFetch<{ saved: true; scene: RenderScene }>(token, "/api/session/render-scene", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(scene),
    }),
  uploadRenderBackground: (token: string, image: Blob) =>
    participantFetch<{ r2Key: string }>(token, "/api/session/render-background", {
      method: "PUT",
      headers: { "content-type": image.type },
      body: image,
    }),
  rtkToken: (token: string) =>
    participantFetch<{ authToken: string; meetingId: string }>(token, "/api/rtk/token", {
      method: "POST",
    }),
};

export async function sha256HexBlob(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
